import React, {
  createContext,
  startTransition,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  DARK_SCHEME_QUERY,
  defaultTheme,
  explicitTheme,
  LEGACY_THEME_KEY,
  PHONE_QUERY,
  THEME_CHOICE_KEY,
  THEME_COLOR,
} from '../lib/theme.js';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const ONE_YEAR_S = 60 * 60 * 24 * 365;

/** The visitor's explicit choice, if any — the toggle, or a legacy `theme=light`. */
const readExplicitChoice = (): Theme | null => {
  try {
    return explicitTheme(
      localStorage.getItem(THEME_CHOICE_KEY),
      localStorage.getItem(LEGACY_THEME_KEY)
    );
  } catch {
    return null;
  }
};

/** What this device gets without a choice (src/lib/theme.js). */
const deviceTheme = (): Theme => {
  const mm = typeof window !== 'undefined' ? window.matchMedia : undefined;
  if (typeof mm !== 'function') return 'dark';
  return defaultTheme({
    phone: mm(PHONE_QUERY).matches,
    prefersDark: mm(DARK_SCHEME_QUERY).matches,
  });
};

const setCookie = (name: string, value: string, maxAge: number) => {
  try {
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; samesite=lax`;
  } catch {
    /* ignore */
  }
};

/**
 * Persist an explicit choice: localStorage for the boot script, a cookie for
 * the server (which emits `<html class>` from it on the next request, so a
 * hard refresh never flashes the other theme). The legacy key goes.
 */
const persistChoice = (theme: Theme) => {
  try {
    localStorage.setItem(THEME_CHOICE_KEY, theme);
    localStorage.removeItem(LEGACY_THEME_KEY);
  } catch {
    /* ignore */
  }
  setCookie(THEME_CHOICE_KEY, theme, ONE_YEAR_S);
  setCookie(LEGACY_THEME_KEY, '', 0);
};

/** A legacy `theme=dark` was the old default written on every visit, not a choice: drop it. */
const dropLegacyDefault = () => {
  try {
    if (localStorage.getItem(LEGACY_THEME_KEY) !== null) localStorage.removeItem(LEGACY_THEME_KEY);
  } catch {
    /* ignore */
  }
  setCookie(LEGACY_THEME_KEY, '', 0);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initial state is deterministic ('dark') on both server and client so
  // hydration matches. The inline boot script in index.html (and the server's
  // theme hint) set the html class before React renders, so the visual theme
  // is right from first paint; the effect below reconciles React state with
  // the actual theme after hydration — one re-render of the toggle icon.
  const [theme, setThemeState] = useState<Theme>('dark');
  // Whether `theme` is the visitor's own choice. Only a choice is ever
  // persisted: until 2026-10-01 the default itself was written to storage on
  // every visit, which is why a stored dark can no longer count as a choice
  // (src/lib/theme.js).
  const explicitRef = useRef(false);
  // The theme the reconcile effect below asked for that has not rendered yet.
  // Until it has, `theme` still holds the hydration default while <html>
  // already carries the right class (boot script + server hint), and the apply
  // effect must not paint that default over it: on a phone it turned the light
  // page dark until the correction rendered, seconds into a slow load
  // (2026-10-02). A toggle or a device change supersedes it.
  const pendingRef = useRef<Theme | null>(null);

  const applyTheme = useCallback((t: Theme) => {
    const root = document.documentElement;
    root.classList.remove('dark', 'light');
    root.classList.add(t);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[t]);
  }, []);

  // After hydration: an explicit choice, else what this device gets — a
  // phone follows its own dark-mode setting, everything wider stays dark.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const choice = readExplicitChoice();
    explicitRef.current = choice !== null;
    if (choice) {
      // A legacy `theme=light` becomes a choice under the new key.
      persistChoice(choice);
    } else {
      dropLegacyDefault();
    }
    const actual = choice ?? deviceTheme();
    if (actual !== theme) {
      pendingRef.current = actual;
      // Only the toggle's icon is behind (the page already shows `actual`),
      // so the correction is a transition: it never forces the page's
      // not-yet-hydrated content to hydrate in one blocking task.
      startTransition(() => setThemeState(actual));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply on every change; persist only a choice. Never the hydration default
  // while a correction is still on its way (pendingRef).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (pendingRef.current !== null) {
      if (theme !== pendingRef.current) return;
      pendingRef.current = null;
    }
    applyTheme(theme);
    if (explicitRef.current) persistChoice(theme);
  }, [theme, applyTheme]);

  // Without a choice the theme follows the device: a phone switching to dark
  // mode at sunset switches the site, and a window resized across the phone
  // boundary re-evaluates. A choice ends the following until storage is
  // cleared — there is no "back to automatic" control, by design (one
  // toggle, two states).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const lists = [window.matchMedia(PHONE_QUERY), window.matchMedia(DARK_SCHEME_QUERY)];
    const follow = () => {
      if (explicitRef.current) return;
      pendingRef.current = null;
      setThemeState(deviceTheme());
    };
    for (const list of lists) list.addEventListener('change', follow);
    return () => {
      for (const list of lists) list.removeEventListener('change', follow);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    explicitRef.current = true;
    pendingRef.current = null;
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setTheme = useCallback((t: Theme) => {
    explicitRef.current = true;
    pendingRef.current = null;
    setThemeState(t);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
